import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, Typography } from "@material-tailwind/react";
import { ServiceFormProps } from "data/reduxInterfaces";
import { Event } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, Fragment, HTMLAttributes, useEffect, useRef } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { fetchEvents } from "redux/features/Admin/Events/eventSlice";
import { getFields } from "redux/features/service/serviceSlice";
import { z } from "zod";
import {
  Cog6ToothIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XMarkIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

const formSchema = z.object({
  equipments: z.array(z.string()).min(0),
  events: z
    .array(z.string())
    .refine((arg) => arg.length > 0, "Merci de sélectionner au moins un type d'événement."),
});

const ServiceExtraDetails: FC<ServiceFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  errorServiceForm,
  FormData,
}) => {
  const dispatch = useAppDispatch();
  const events: Event[] = useAppSelector((state) => state.featureEvent.events);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { equipments: [], events: [] },
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  const prevHandleForm = useRef(handleForm);

  useEffect(() => {
    dispatch(getFields());
    dispatch(fetchEvents());
  }, [dispatch]);

  useEffect(() => {
    if (handleForm.type === "chekServiceFormTwo" && prevHandleForm.current !== handleForm) {
      handleSubmit(onSubmit)();
      prevHandleForm.current = handleForm;
    }
  }, [handleForm, handleSubmit, onSubmit]);

  // Grouper les événements par catégorie si possible (simulation)
  const eventCategories = [
    {
      name: "Mariages & Célébrations",
      events:
        events?.filter(
          (e) =>
            e.designation.toLowerCase().includes("mariage") ||
            e.designation.toLowerCase().includes("célébration") ||
            e.designation.toLowerCase().includes("noce")
        ) || [],
    },
    {
      name: "Entreprise & Professionnel",
      events:
        events?.filter(
          (e) =>
            e.designation.toLowerCase().includes("entreprise") ||
            e.designation.toLowerCase().includes("professionnel") ||
            e.designation.toLowerCase().includes("séminaire") ||
            e.designation.toLowerCase().includes("conférence")
        ) || [],
    },
    {
      name: "Événements privés",
      events:
        events?.filter(
          (e) =>
            e.designation.toLowerCase().includes("anniversaire") ||
            e.designation.toLowerCase().includes("privé") ||
            e.designation.toLowerCase().includes("famille")
        ) || [],
    },
  ];

  // Événements non catégorisés
  const categorizedEventNames = eventCategories.flatMap((cat) => cat.events.map((e) => e.designation));
  const uncategorizedEvents = events?.filter((e) => !categorizedEventNames.includes(e.designation)) || [];

  return (
    <Fragment>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {/* Header moderne avec icône */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-eventToBee/10 rounded-xl">
            <Cog6ToothIcon className="w-6 h-6 text-eventToBee" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Spécifications de la prestation</h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Sélectionnez les types d'événements pour lesquels vous proposez vos services
            </p>
          </div>
        </div>

        <div className="space-y-8">
          {/* Section Types d'événements */}
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <CalendarDaysIcon className="w-5 h-5 text-eventToBee" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Types d'événements *</h3>
            </div>

            <Controller
              name="events"
              control={control}
              render={({ field }) => {
                const { value, onChange } = field;

                const handleCheckboxChange = (eventDesignation: string) => {
                  let updatedValue;
                  if (value.includes(eventDesignation)) {
                    updatedValue = value.filter((v) => v !== eventDesignation);
                  } else {
                    updatedValue = [...value, eventDesignation];
                  }
                  onChange(updatedValue);
                };

                const EventCard = ({ event, isSelected }: { event: Event; isSelected: boolean }) => (
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
                      ${
                        isSelected
                          ? "border-eventToBee bg-eventToBee/5"
                          : "border-gray-200 hover:border-eventToBee/50 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }
                    `}
                    onClick={() => handleCheckboxChange(event.designation)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                          ${isSelected ? "bg-eventToBee border-eventToBee" : "border-gray-300"}
                        `}
                        >
                          {isSelected && <CheckCircleIcon className="w-3 h-3 text-white" />}
                        </div>
                        <span
                          className={`font-medium ${
                            isSelected ? "text-eventToBee" : "text-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {event.designation}
                        </span>
                      </div>
                    </div>

                    {isSelected && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
                      >
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      </motion.div>
                    )}
                  </motion.div>
                );

                return (
                  <div className="space-y-6">
                    {/* Événements catégorisés */}
                    {eventCategories.map((category, catIndex) => {
                      if (category.events.length === 0) return null;

                      return (
                        <motion.div
                          key={category.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: catIndex * 0.1 }}
                          className="space-y-3"
                        >
                          <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm uppercase tracking-wide">
                            {category.name}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {category.events.map((event) => (
                              <EventCard
                                key={event.designation}
                                event={event}
                                isSelected={value.includes(event.designation)}
                              />
                            ))}
                          </div>
                        </motion.div>
                      );
                    })}

                    {/* Autres événements */}
                    {uncategorizedEvents.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="space-y-3"
                      >
                        <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm uppercase tracking-wide">
                          Autres événements
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {uncategorizedEvents.map((event) => (
                            <EventCard
                              key={event.designation}
                              event={event}
                              isSelected={value.includes(event.designation)}
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                );
              }}
            />

            {/* Affichage des erreurs */}
            {errors.events && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <div className="flex items-center space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-800 text-sm font-medium">{errors.events.message}</p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Résumé de la sélection */}
          <Controller
            name="events"
            control={control}
            render={({ field: { value } }) => {
              if (value.length === 0) return <></>;

              return (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 rounded-xl p-4"
                >
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-2">
                        {value.length} type{value.length > 1 ? "s" : ""} d'événement{value.length > 1 ? "s" : ""}{" "}
                        sélectionné{value.length > 1 ? "s" : ""}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {value.map((eventType, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium"
                          >
                            {eventType}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            }}
          />

          {/* Conseils d'aide */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-4"
          >
            <div className="flex items-start space-x-3">
              <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Conseils pour bien choisir</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Sélectionnez tous les types d'événements pour lesquels vous avez de l'expérience</li>
                  <li>• Plus vous proposez de types, plus vous aurez de demandes</li>
                  <li>• Vous pourrez toujours modifier ces choix plus tard</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Fragment>
  );
};

export default ServiceExtraDetails;
